                                                                                                                                                                                                                                                                                                                                                                                                                                         import React, { useState, useEffect, useCallback } from 'react';
import { GraphQLPMSService } from '../../services/graphqlPms';
import { ReviewCycle, ReviewCycleStatus } from '../../types/pms';
import InlineLoader from '../common/InlineLoader';
import AppDialog, { AppDialogTone } from '../common/AppDialog';
import RightSidePanel from './RightSidePanel';
import './AnnualObjectiveCycle.css';

interface AnnualCycleForm {
  name: string;
  startDate: string;
  endDate: string;
  submissionDeadline: string;
}

const EMPTY_FORM: AnnualCycleForm = {
  name: '',
  startDate: '',
  endDate: '',
  submissionDeadline: '',
};

const AnnualObjectiveCycle: React.FC = () => {
  const [annualCycles, setAnnualCycles] = useState<ReviewCycle[]>([]);
  const [reviewCycleCounts, setReviewCycleCounts] = useState<Record<string, number>>({});
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<AnnualCycleForm>(EMPTY_FORM);
  const [errors, setErrors] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);

  /*
   * window.confirm renders browser chrome pinned to the top of the viewport, in the OS font,
   * ignoring the app theme — and it blocks the JS thread, so nothing repaints behind it.
   * AppDialog is the shared centred, themed replacement already used by ReviewCycleConfig.
   *
   * Promise-wrapped so both call sites keep reading top-to-bottom exactly as they did with
   * `if (!window.confirm(...)) return;`, instead of each handler splitting into a "request"
   * half and a "do it" callback.
   */
  const [confirmDialog, setConfirmDialog] = useState<{
    title: string;
    message: React.ReactNode;
    tone: AppDialogTone;
    confirmLabel: string;
    resolve: (confirmed: boolean) => void;
  } | null>(null);

  const confirmAction = useCallback(
    (opts: { title: string; message: React.ReactNode; tone: AppDialogTone; confirmLabel: string }) =>
      new Promise<boolean>(resolve => setConfirmDialog({ ...opts, resolve })),
    []
  );

  const settleConfirm = useCallback((confirmed: boolean) => {
    setConfirmDialog(prev => {
      prev?.resolve(confirmed);
      return null;
    });
  }, []);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const allCycles = await GraphQLPMSService.getAllReviewCycles();
      // Annual cycles = no annualCycleId
      const parents = allCycles.filter(c => !c.annualCycleId);
      // Count child review cycles per annual cycle
      const counts: Record<string, number> = {};
      for (const parent of parents) {
        counts[parent.id] = allCycles.filter(c => c.annualCycleId === parent.id).length;
      }
      setAnnualCycles(parents);
      setReviewCycleCounts(counts);
    } catch (err: any) {
      console.error('[AnnualObjectiveCycle] Load error:', err);
      // Attempt partial recovery from error data
      const partial = err?.data?.listPMSReviewCycles?.items;
      if (partial && Array.isArray(partial)) {
        const validItems = partial.filter((c: any) => c && c.id);
        const parents = validItems.filter((c: any) => !c.annualCycleId);
        setAnnualCycles(parents.map((c: any) => ({
          id: c.id,
          name: c.name || '',
          startDate: c.startDate || '',
          endDate: c.endDate || '',
          midYearDeadline: c.midYearDeadline || '',
          endYearDeadline: c.endYearDeadline || '',
          submissionDeadline: c.submissionDeadline || '',
          status: c.status || 'Draft',
          isLocked: c.isLocked ?? false,
          annualCycleId: c.annualCycleId || undefined,
          createdAt: c.createdAt || '',
          updatedAt: c.updatedAt || '',
        })));
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const validate = (): string[] => {
    const errs: string[] = [];
    if (!form.name.trim()) errs.push('Cycle name is required');
    if (!form.startDate) errs.push('Start date is required');
    if (!form.endDate) errs.push('End date is required');
    if (form.startDate && form.endDate && form.startDate >= form.endDate) errs.push('End date must be after start date');
    if (form.submissionDeadline && form.startDate && form.submissionDeadline < form.startDate) errs.push('Submission deadline must be after start date');
    return errs;
  };

  const handleSubmit = async () => {
    const errs = validate();
    if (errs.length > 0) { setErrors(errs); return; }
    setErrors([]);
    setSaving(true);
    try {
      if (editingId) {
        await GraphQLPMSService.updateReviewCycle(editingId, {
          name: form.name,
          startDate: form.startDate,
          endDate: form.endDate,
          submissionDeadline: form.submissionDeadline || undefined,
        });
      } else {
        // Create as annual cycle (no annualCycleId)
        await GraphQLPMSService.createReviewCycle({
          name: form.name,
          startDate: form.startDate,
          endDate: form.endDate,
          submissionDeadline: form.submissionDeadline || undefined,
        });
      }
      setForm(EMPTY_FORM);
      setShowForm(false);
      setEditingId(null);
      await loadData();
    } catch (err) {
      setErrors(['Failed to save cycle. Please try again.']);
    } finally {
      setSaving(false);
    }
  };

  const handlePublish = async (cycleId: string) => {
    const confirmed = await confirmAction({
      title: 'Publish this annual cycle?',
      message: 'Employees will be able to create goals for this period. You can then create review cycles under it.',
      tone: 'info',
      confirmLabel: 'Publish',
    });
    if (!confirmed) return;
    setSaving(true);
    await GraphQLPMSService.updateReviewCycle(cycleId, { status: ReviewCycleStatus.ACTIVE });
    await loadData();
    setSaving(false);
  };

  const handleComplete = async (cycleId: string) => {
    const confirmed = await confirmAction({
      title: 'Mark this annual cycle as completed?',
      message: 'No new goals can be created and all child review cycles will be finalized.',
      tone: 'warning',
      confirmLabel: 'Mark as Completed',
    });
    if (!confirmed) return;
    setSaving(true);
    await GraphQLPMSService.updateReviewCycle(cycleId, { status: ReviewCycleStatus.COMPLETED });
    await loadData();
    setSaving(false);
  };

  const handleEdit = (cycle: ReviewCycle) => {
    setForm({
      name: cycle.name,
      startDate: cycle.startDate,
      endDate: cycle.endDate,
      submissionDeadline: cycle.submissionDeadline || '',
    });
    setEditingId(cycle.id);
    setShowForm(true);
    setErrors([]);
  };

  const handleCancel = () => {
    setForm(EMPTY_FORM);
    setShowForm(false);
    setEditingId(null);
    setErrors([]);
  };

  const formatDate = (d: string) => d ? new Date(d + 'T00:00:00').toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }) : '—';

  const getStatusClass = (s: ReviewCycleStatus) => {
    if (s === ReviewCycleStatus.ACTIVE) return 'aoc-status--active';
    if (s === ReviewCycleStatus.COMPLETED) return 'aoc-status--completed';
    if (s === ReviewCycleStatus.LOCKED) return 'aoc-status--locked';
    return 'aoc-status--draft';
  };

  const hasActiveCycle = annualCycles.some(c => c.status === ReviewCycleStatus.ACTIVE);

  if (loading) return <InlineLoader message="Loading annual cycles" />;

  return (
    <div className="aoc-page">
      <div className="aoc-header">
        <div>
          <h1 className="aoc-title">Annual Objective Cycles</h1>
          <p className="aoc-subtitle">Create and publish annual cycles for employee goal setting. Review cycles are created separately under each published annual cycle.</p>
        </div>
        {!showForm && (
          <button className="aoc-btn aoc-btn--primary" onClick={() => { setShowForm(true); setEditingId(null); setForm(EMPTY_FORM); }}>
            + New Annual Cycle
          </button>
        )}
      </div>

      {hasActiveCycle && (
        <div className="aoc-active-banner">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><path d="M12 8v4l3 3"/></svg>
          An active cycle is currently published. Employees can create goals. Go to <strong>Review Cycle Configuration</strong> to create review cycles under it.
        </div>
      )}

      {showForm && (
        <RightSidePanel
          isOpen={showForm}
          title={editingId ? 'Edit Annual Cycle' : 'Create Annual Cycle'}
          onClose={handleCancel}
          wide
        >
          <div className="aoc-form-card">
            {errors.length > 0 && (
              <div className="aoc-form-errors">{errors.map((e, i) => <p key={i}>{e}</p>)}</div>
            )}
            <div className="aoc-form-grid">
              <div className="aoc-form-field aoc-form-field--full">
                <label>Cycle Name</label>
                <input type="text" value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} placeholder="e.g. FY 2026-27 (April 2026 – March 2027)" />
              </div>
              <div className="aoc-form-field">
                <label>Start Date</label>
                <input type="date" value={form.startDate} onChange={e => setForm(p => ({ ...p, startDate: e.target.value }))} />
              </div>
              <div className="aoc-form-field">
                <label>End Date</label>
                <input type="date" value={form.endDate} onChange={e => setForm(p => ({ ...p, endDate: e.target.value }))} />
              </div>
              <div className="aoc-form-field aoc-form-field--full">
                <label>Goal Submission Deadline <span className="aoc-optional">(optional)</span></label>
                <input type="date" value={form.submissionDeadline} onChange={e => setForm(p => ({ ...p, submissionDeadline: e.target.value }))} />
                <span className="aoc-field-hint">Deadline for employees to submit their goals for this annual cycle</span>
              </div>
            </div>
            <div className="aoc-form-actions">
              <button className="aoc-btn aoc-btn--outline" onClick={handleCancel}>Cancel</button>
              <button className="aoc-btn aoc-btn--primary" onClick={handleSubmit} disabled={saving}>
                {saving ? 'Saving…' : editingId ? 'Save Changes' : 'Create Cycle'}
              </button>
            </div>
          </div>
        </RightSidePanel>
      )}

      <div className="aoc-list">
        {annualCycles.length === 0 ? (
          <div className="aoc-empty">
            <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="1.5"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
            <p>No annual cycles created yet</p>
            <span>Click "+ New Annual Cycle" to get started</span>
          </div>
        ) : (
          annualCycles.sort((a, b) => b.startDate.localeCompare(a.startDate)).map(cycle => (
            <div key={cycle.id} className={`aoc-card ${cycle.status === ReviewCycleStatus.ACTIVE ? 'aoc-card--active' : ''}`}>
              <div className="aoc-card-header">
                <div className="aoc-card-header__left">
                  <h3 className="aoc-card-name">{cycle.name}</h3>
                  <span className="aoc-card-period">{formatDate(cycle.startDate)} - {formatDate(cycle.endDate)}</span>
                </div>
                <span className={`aoc-status ${getStatusClass(cycle.status)}`}>{cycle.status}</span>
              </div>
              <div className="aoc-card-meta">
                {cycle.submissionDeadline && (
                  <div className="aoc-meta-item">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
                    <span>Goal Submission: {formatDate(cycle.submissionDeadline)}</span>
                  </div>
                )}
                <div className="aoc-meta-item">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
                  <span>{reviewCycleCounts[cycle.id] || 0} review cycle{(reviewCycleCounts[cycle.id] || 0) !== 1 ? 's' : ''} created</span>
                </div>
              </div>
              <div className="aoc-card-actions">
                {cycle.status === ReviewCycleStatus.DRAFT && (
                  <>
                    <button className="aoc-btn aoc-btn--outline aoc-btn--sm" onClick={() => handleEdit(cycle)}>Edit</button>
                    <button className="aoc-btn aoc-btn--success aoc-btn--sm" onClick={() => handlePublish(cycle.id)} disabled={hasActiveCycle || saving}>
                      {hasActiveCycle ? 'Another cycle is active' : 'Publish to All Employees'}
                    </button>
                  </>
                )}
                {cycle.status === ReviewCycleStatus.ACTIVE && (
                  <>
                    <button className="aoc-btn aoc-btn--outline aoc-btn--sm" onClick={() => handleEdit(cycle)}>Edit</button>
                    <button className="aoc-btn aoc-btn--outline aoc-btn--sm aoc-btn--danger" onClick={() => handleComplete(cycle.id)} disabled={saving}>
                      Mark as Completed
                    </button>
                  </>
                )}
              </div>
            </div>
          ))
        )}
      </div>

      <AppDialog
        open={!!confirmDialog}
        title={confirmDialog?.title ?? ''}
        message={confirmDialog?.message ?? ''}
        tone={confirmDialog?.tone ?? 'info'}
        confirmLabel={confirmDialog?.confirmLabel ?? 'OK'}
        cancelLabel="Cancel"
        onConfirm={() => settleConfirm(true)}
        onCancel={() => settleConfirm(false)}
      />
    </div>
  );
};

export default AnnualObjectiveCycle;
